import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { useOrder, Service } from '../context/OrderContext'
import { useAuth } from '../context/AuthContext'
import Stepper from "../components/Stepper";
import OrderSummary from "../components/OrderSummary";

const ServiceSelectionPage: React.FC = () => {
  const navigate = useNavigate()
  const { setService, resetOrder } = useOrder()
  const { user } = useAuth()

  const [services, setServices] = useState<Service[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { goToStep } = useOrder()
  useEffect(() => {
    console.log('[ServiceSelectionPage] mount → chargement des services')
    let mounted = true
    
    // Timeout de sécurité pour éviter un loading infini
    const timeoutId = setTimeout(() => {
      if (mounted && loading) {
        console.error('[ServiceSelectionPage] Timeout: chargement trop long')
        setLoading(false)
        setError('Le chargement a pris trop de temps. Vérifiez votre connexion.')
      }
    }, 10000) // 10 secondes max
     
    const loadServices = async () => {
      try {
        setLoading(true)
        setError(null)

        const { data, error } = await supabase
          .from('services')
          .select('*')
          .eq('is_active', true)
          .order('name', { ascending: true })

        // console.log('[ServiceSelectionPage] réponse Supabase:', { data, error })

        if (error) {
          console.error('Erreur chargement services', error)
          if (mounted) {
            setError('Erreur lors du chargement des services.')
            setServices([])
          }
        } else {
          if (mounted) {
            setServices((data ?? []) as Service[])
          }
        }
      } catch (e) {
        console.error('Exception loadServices', e)
        if (mounted) {
          setError('Erreur inattendue lors du chargement.')
        }
      } finally {
        if (mounted) {
          setLoading(false)
          clearTimeout(timeoutId)
        }
      }
    }
    
    goToStep('SERVICE') // force l'étape courante au montage
    resetOrder() // on nettoie le draft UNE FOIS
    void loadServices()
    
    return () => {
      mounted = false
      clearTimeout(timeoutId)
    }
    // ⬇️ important : tableau de dépendances vide

  }, []) // eslint-disable-line react-hooks/exhaustive-deps // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectService = (service: Service) => {
    console.log('[ServiceSelectionPage] service sélectionné', service)
    setService(service)
    navigate('/order/options')
  }

  if (loading) {
    return (
      <div className="card">
        <Stepper />
        <div className="card-header">
          <h1 className="card-title">Chargement des services…</h1>
        </div>
      </div>
    )
  }

  return (
    <div className="card">
      <Stepper />
      <div className="card-header" style={{ justifyContent: 'space-between' }}>
        <h1 className="card-title">Choisis ton service et démarre ta commande</h1>
      </div>

      {error && (
        <p style={{ color: '#f87171', marginTop: '0.5rem' }}>
          {error} Vérifie la console navigateur pour plus de détails.
        </p>
      )}

      <div className="grid-2" style={{ marginTop: '1rem' }}>
        {services.map(service => (
          <button
            key={service.id}
            type="button"
            data-testid={`service-button-${service.id}`}
            className="card"
            style={{
              textAlign: 'left',
              cursor: 'pointer',
              border: '1px solid rgba(148,163,184,0.3)',
              padding: 0,
              overflow: 'hidden',
            }}
            onClick={() => handleSelectService(service)}
          >
            {service.image_url && (
              <img
                data-testid={`service-image-${service.id}`}
                src={service.image_url}
                alt={service.name}
                style={{
                  width: '100%',
                  height: 140,
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            )}
            <div style={{ padding: '1.25rem' }}>
              <div className="card-header" style={{ marginBottom: '.5rem' }}>
                <h2 className="card-title" style={{ fontSize: '1rem' }}>
                  {service.name}
                </h2>
                <div
                  style={{
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: '#22c55e',
                    marginLeft: 'auto',
                  }}
                >
                  {service.base_price.toFixed(2)} CHF
                </div>
              </div>
              <p
                style={{
                  fontSize: '.9rem',
                  color: '#9ca3af',
                  lineHeight: 1.4,
                }}
              >
                {service.description ||
                  "Service complet de cordage avec tension au choix et conseils personnalisés."}
              </p>
            </div>
          </button>
        ))}
      </div>

      {!error && !loading && services.length === 0 && (
        <p style={{ color: '#9ca3af', marginTop: '1rem' }}>
          Aucun service n’est actuellement disponible.
          Vérifie la table <code>services</code> et la colonne <code>is_active</code>.
        </p>
      )}

      {/* Panneau récapitulatif */}
      <div style={{ flex: '0 0 320px', minWidth: 280 }}>
        <OrderSummary />
      </div>
    </div>
  )
}

export default ServiceSelectionPage
